import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/CatalogSheet.css";
import CatalogProductGrid from "../components/catalog/CatalogProductGrid";
import AlternativeProductsBlock from "../components/AlternativeProductsBlock";
import CatalogSheetHeader from "../components/catalog/CatalogSheetHeader";
import CatalogCategoryTabs from "../components/catalog/CatalogCategoryTabs";
import CatalogCategoryList from "../components/catalog/CatalogCategoryList";
import useCatalogSheetNavigation from "../components/catalog/useCatalogSheetNavigation";
import { trackEvent } from "../services/amplitude";

const ANIM_MS = 450;
const CLOSE_DRAG_THRESHOLD = 120;
const MAX_DRAG_Y = 420;

export default function CatalogSheet({
  open,
  onClose,
  topOffset = 84,
  onOpenProduct,
  onAction,
  featuredTabIcon = "",
}) {
  const { t, i18n } = useTranslation("catalog");

  const lang = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("en")
    ? "en"
    : "uk";

  const sheetRef = useRef(null);
  const dragStartYRef = useRef(0);
  const dragDeltaYRef = useRef(0);
  const draggingRef = useRef(false);

  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const [dragY, setDragY] = useState(0);

  const {
    loading,
    title,
    mode,
    categories,
    products,
    stack,
    topCategories,
    activeTopCategoryId,
    selectTopCategory,
    openNestedCategory,
    goBack,
  } = useCatalogSheetNavigation({
    open,
    lang,
    t,
  });

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      setDragY(0);
      draggingRef.current = false;
      dragDeltaYRef.current = 0;
      return;
    }

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
        setDragY(0);
      }, ANIM_MS);

      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }

    if (mounted) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  function handleProductBlockAction(a) {
  if (a?.type === "open_product") {
    return onOpenProduct?.(a.product);
  }

  if (a?.type === "add_to_cart") {
    const p = a.product || {};

    trackEvent("cart_item_added", {
      product_id: p?.product_id || p?.id || a?.product_id || null,
      product_name: p?.name || p?.name_uk || p?.name_en || null,
      variant_id: p?.variant_id || p?.id || null,
      template_id: p?.template_id || null,
      categories: Array.isArray(p?.categories) ? p.categories.slice(0, 5) : [],
      lang,
      source: "catalog",
      qty: Number(a?.qty || 1),
      price: Number(p?.price ?? 0),
    });
  }

  return onAction?.(a);
}

  function clamp(n, min, max) {
    return Math.max(min, Math.min(n, max));
  }

  function handleDragStart(clientY) {
    if (closing) return;
    draggingRef.current = true;
    dragStartYRef.current = clientY;
    dragDeltaYRef.current = 0;
  }

  function handleDragMove(clientY) {
    if (!draggingRef.current) return;

    const delta = clientY - dragStartYRef.current;
    const next = clamp(delta, 0, MAX_DRAG_Y);

    dragDeltaYRef.current = next;
    setDragY(next);
  }

  function handleDragEnd() {
    if (!draggingRef.current) return;

    draggingRef.current = false;
    const shouldClose = dragDeltaYRef.current > CLOSE_DRAG_THRESHOLD;

    if (shouldClose) {
      setDragY(0);
      onClose?.();
      return;
    }

    dragDeltaYRef.current = 0;
    setDragY(0);
  }

  function onPointerDown(e) {
    handleDragStart(e.clientY);
  }

  function onPointerMove(e) {
    handleDragMove(e.clientY);
  }

  function onPointerUp() {
    handleDragEnd();
  }

  function onPointerCancel() {
    handleDragEnd();
  }

  if (!mounted) return null;

  const overlayClass = `csOverlay ${open && !closing ? "isOpen" : "isClosing"}`;
  const sheetStyle = dragY
    ? {
        transform: `translateY(${dragY}px)`,
        transition: "none",
      }
    : undefined;

  const canGoBack = Boolean(stack.length);

  return (
    <div
      className={overlayClass}
      style={{ paddingTop: topOffset }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="csSheet"
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        style={sheetStyle}
      >
        <CatalogSheetHeader
          title={title}
          canGoBack={canGoBack}
          onBack={goBack}
          onClose={onClose}
          t={t}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        />

        <CatalogCategoryTabs
          visible={Boolean(topCategories.length)}
          icon={featuredTabIcon}
          items={topCategories}
          activeId={activeTopCategoryId}
          onSelect={selectTopCategory}
        />

        <div
          className="csContent"
          style={{ paddingBottom: `calc(80px + env(safe-area-inset-bottom, 0px))` }}
        >
          {loading ? (
            <div className="catGrid">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="pCard catCard skeletonCard">
                  <div className="skeletonImg" />
                  <div className="skeletonLine" />
                  <div className="skeletonLine short" />
                  <div className="skeletonBtn" />
                </div>
              ))}
            </div>
          ) : mode === "categories" ? (
            categories?.length ? (
              <CatalogCategoryList
                categories={categories}
                onOpen={openNestedCategory}
              />
            ) : (
              <div className="csEmpty">{t("empty_categories")}</div>
            )
          ) : products?.length ? (
            <>
              <CatalogProductGrid
                title={t("products_title")}
                products={products}
                onAction={handleProductBlockAction}
                perPage={10}
              />

              <AlternativeProductsBlock
                onOpen={(p) => onOpenProduct?.(p)}
                onAdd={(p) =>
                  handleProductBlockAction({
                    type: "add_to_cart",
                    product_id: p.variant_id || p.id,
                    product: { ...p, id: p.variant_id || p.id },
                    qty: 1,
                  })
                }
              />
            </>
          ) : (
            <div className="csEmpty">{t("empty_products")}</div>
          )}
        </div>
      </div>
    </div>
  );
}